import React, { useState, useEffect, useRef } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Calendar,
  ChevronRight,
  ChevronDown,
  Download
} from 'lucide-react';
import logger from '../utils/logger';

const GanttChart = ({ tasks, onTaskUpdate, onTaskClick }) => {
  const [zoomLevel, setZoomLevel] = useState('week');
  const [viewStartDate, setViewStartDate] = useState(null);
  const [viewEndDate, setViewEndDate] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [draggingTask, setDraggingTask] = useState(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragMode, setDragMode] = useState(null); // 'move', 'resize-left', 'resize-right'
  const [localTasks, setLocalTasks] = useState(tasks); // Local copy of tasks for optimistic updates
  const chartRef = useRef(null);
  const headerRef = useRef(null);
  const taskNamesRef = useRef(null);

  // Sync local tasks when parent tasks change
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  const ROW_HEIGHT = 60;
  const TASK_NAME_WIDTH = window.innerWidth < 640 ? 200 : 350;

  // Get column width based on zoom
  const getColumnWidth = () => {
    switch (zoomLevel) {
      case 'day': return 100;
      case 'week': return 80;
      case 'month': return 50;
      case 'quarter': return 40;
      default: return 80;
    }
  };

  const columnWidth = getColumnWidth();

  // Parse date safely - handle YYYY-MM-DD format to avoid timezone issues
  const parseDate = (dateStr) => {
    if (!dateStr) return null;

    // If date is in YYYY-MM-DD format, parse it explicitly in local timezone
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get date range from tasks - extend range to allow scrolling in both directions
  const getDateRange = () => {
    const validTasks = localTasks.filter(t => t.due_date || t.created_at);
    if (validTasks.length === 0) {
      const today = new Date();
      return {
        start: new Date(today.getFullYear() - 2, 0, 1), // 2 years before
        end: new Date(today.getFullYear() + 2, 11, 31)   // 2 years after
      };
    }

    let minDate = new Date();
    let maxDate = new Date();

    validTasks.forEach(task => {
      const dueDate = parseDate(task.due_date);
      const createdDate = parseDate(task.created_at);

      if (dueDate) {
        if (dueDate < minDate) minDate = dueDate;
        if (dueDate > maxDate) maxDate = dueDate;
      }
      if (createdDate) {
        if (createdDate < minDate) minDate = createdDate;
        if (createdDate > maxDate) maxDate = createdDate;
      }
    });

    // Extend range significantly to allow scrolling backwards and forwards
    minDate.setFullYear(minDate.getFullYear() - 1); // 1 year before earliest task
    maxDate.setFullYear(maxDate.getFullYear() + 1); // 1 year after latest task

    return { start: minDate, end: maxDate };
  };

  // Initialize view dates - recalculate when localTasks change
  useEffect(() => {
    const range = getDateRange();
    setViewStartDate(range.start);
    setViewEndDate(range.end);
  }, [localTasks]);

  // Scroll to current date when chart loads or view dates change
  useEffect(() => {
    if (viewStartDate && viewEndDate && chartRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const today = new Date();
        const todayX = getXForDate(today);
        // Scroll to center today in the viewport
        if (chartRef.current) {
          chartRef.current.scrollLeft = todayX - chartRef.current.clientWidth / 2;
        }
      }, 100);
    }
  }, [viewStartDate, viewEndDate]);

  // Generate time columns
  const generateTimeColumns = () => {
    if (!viewStartDate || !viewEndDate) return [];

    const columns = [];
    const current = new Date(viewStartDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    while (current <= viewEndDate) {
      const isToday = current.toDateString() === today.toDateString();
      const isWeekend = current.getDay() === 0 || current.getDay() === 6;

      columns.push({
        date: new Date(current),
        label: formatDate(current),
        isToday,
        isWeekend
      });

      switch (zoomLevel) {
        case 'day':
          current.setDate(current.getDate() + 1);
          break;
        case 'week':
          current.setDate(current.getDate() + 7);
          break;
        case 'month':
          // Set to first of month to avoid date overflow issues (e.g., Jan 31 -> Feb 31 = Mar 3)
          current.setDate(1);
          current.setMonth(current.getMonth() + 1);
          break;
        case 'quarter':
          // Set to first of month to avoid date overflow issues
          current.setDate(1);
          current.setMonth(current.getMonth() + 3);
          break;
        default:
          current.setDate(current.getDate() + 7);
      }
    }

    return columns;
  };

  const timeColumns = generateTimeColumns();

  // Generate year headers based on time columns
  const generateYearHeaders = () => {
    if (timeColumns.length === 0) return [];

    const yearHeaders = [];
    let currentYear = null;
    let yearStart = 0;
    let columnCount = 0;

    timeColumns.forEach((col, idx) => {
      const year = col.date.getFullYear();

      if (currentYear === null) {
        currentYear = year;
        yearStart = idx;
        columnCount = 1;
      } else if (year === currentYear) {
        columnCount++;
      } else {
        // Year changed, save the previous year header
        yearHeaders.push({
          year: currentYear,
          start: yearStart,
          count: columnCount,
          width: columnCount * columnWidth
        });
        // Start new year
        currentYear = year;
        yearStart = idx;
        columnCount = 1;
      }
    });

    // Add the last year
    if (currentYear !== null) {
      yearHeaders.push({
        year: currentYear,
        start: yearStart,
        count: columnCount,
        width: columnCount * columnWidth
      });
    }

    return yearHeaders;
  };

  const yearHeaders = generateYearHeaders();

  // Get X position for a date
  const getXForDate = (date) => {
    if (!date || !viewStartDate) return 0;

    // Normalize dates to midnight for consistent calculations
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    const normalizedStart = new Date(viewStartDate);
    normalizedStart.setHours(0, 0, 0, 0);

    const diffTime = normalizedDate - normalizedStart;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    let position;
    switch (zoomLevel) {
      case 'day':
        // Each column represents 1 day
        position = diffDays * columnWidth;
        break;
      case 'week':
        // Each column represents 7 days
        position = (diffDays / 7) * columnWidth;
        break;
      case 'month':
        // Each column represents 30 days (approximate month)
        position = (diffDays / 30) * columnWidth;
        break;
      case 'quarter':
        // Each column represents 90 days (approximate quarter)
        position = (diffDays / 90) * columnWidth;
        break;
      default:
        position = (diffDays / 7) * columnWidth;
    }

    return position;
  };

  // Get task bar dimensions
  const getTaskBarDimensions = (task) => {
    const startDate = parseDate(task.start_date) || parseDate(task.created_at) || new Date();
    const endDate = parseDate(task.due_date) || new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);

    const x = getXForDate(startDate);
    const endX = getXForDate(endDate);
    const width = Math.max(endX - x, 30);

    return { x, width, startDate, endDate };
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'not_started': 'bg-gray-200 border-gray-400',
      'in_progress': 'bg-blue-200 border-blue-500',
      'under_review': 'bg-yellow-200 border-yellow-500',
      'completed': 'bg-green-200 border-green-500',
      'not_relevant': 'bg-gray-100 border-gray-300'
    };
    return colors[status] || colors.not_started;
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-500',
      'medium': 'bg-orange-500',
      'low': 'bg-green-500'
    };
    return colors[priority] || colors.medium;
  };

  // Group tasks
  const groupedTasks = localTasks.reduce((acc, task) => {
    const category = task.question?.category_display || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(task);
    return acc;
  }, {});

  // Get visible tasks
  const getVisibleTasks = () => {
    const visible = [];
    Object.entries(groupedTasks).forEach(([category, categoryTasks]) => {
      visible.push({ isGroup: true, name: category, count: categoryTasks.length });
      if (!collapsedGroups[category]) {
        categoryTasks.forEach(task => visible.push({ isGroup: false, ...task }));
      }
    });
    return visible;
  };

  const visibleTasks = getVisibleTasks();

  // Toggle group
  const toggleGroup = (category) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Zoom controls
  const handleZoomIn = () => {
    const levels = ['quarter', 'month', 'week', 'day'];
    const currentIndex = levels.indexOf(zoomLevel);
    if (currentIndex < levels.length - 1) {
      setZoomLevel(levels[currentIndex + 1]);
    }
  };

  const handleZoomOut = () => {
    const levels = ['quarter', 'month', 'week', 'day'];
    const currentIndex = levels.indexOf(zoomLevel);
    if (currentIndex > 0) {
      setZoomLevel(levels[currentIndex - 1]);
    }
  };

  const handleFitToScreen = () => {
    setZoomLevel('week');
    const range = getDateRange();
    setViewStartDate(range.start);
    setViewEndDate(range.end);
  };

  const handleJumpToToday = () => {
    const today = new Date();
    const range = getDateRange();
    setViewStartDate(range.start);
    setViewEndDate(range.end);

    setTimeout(() => {
      const todayX = getXForDate(today);
      if (chartRef.current) {
        chartRef.current.scrollLeft = todayX - 200;
      }
    }, 100);
  };

  const handleExport = () => {
    alert('Export functionality would capture the Gantt chart as PNG/PDF.');
  };

  // Calculate progress
  const getProgress = (task) => {
    return task.status === 'completed' ? 100 :
           task.status === 'in_progress' ? 50 :
           task.status === 'under_review' ? 75 : 0;
  };

  // Drag and drop handlers
  const handleTaskBarMouseDown = (e, task, mode = 'move') => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingTask(task);
    setDragStartX(e.clientX);
    setDragOffset(0);
    setDragMode(mode);
  };

  // Add mouse event listeners
  useEffect(() => {
    const handleMove = (e) => {
      if (!draggingTask) return;
      const currentOffset = e.clientX - dragStartX;
      setDragOffset(currentOffset);
    };

    const handleUp = async () => {
      if (!draggingTask || dragOffset === 0) {
        setDraggingTask(null);
        setDragOffset(0);
        setDragMode(null);
        return;
      }

      // Calculate the date shift based on zoom level and drag distance
      let daysToShift = 0;

      switch (zoomLevel) {
        case 'day':
          daysToShift = Math.round(dragOffset / columnWidth);
          break;
        case 'week':
          daysToShift = Math.round(dragOffset / columnWidth) * 7;
          break;
        case 'month':
          daysToShift = Math.round(dragOffset / columnWidth) * 15;
          break;
        case 'quarter':
          daysToShift = Math.round(dragOffset / columnWidth) * 30;
          break;
        default:
          daysToShift = Math.round(dragOffset / columnWidth) * 7;
      }

      if (daysToShift === 0) {
        setDraggingTask(null);
        setDragOffset(0);
        setDragMode(null);
        return;
      }

      const updates = {};

      if (dragMode === 'move') {
        // Move both start and end dates
        const currentStartDate = draggingTask.start_date ? new Date(draggingTask.start_date) : (draggingTask.created_at ? new Date(draggingTask.created_at) : new Date());
        const currentDueDate = draggingTask.due_date ? new Date(draggingTask.due_date) : new Date();

        const newStartDate = new Date(currentStartDate);
        newStartDate.setDate(currentStartDate.getDate() + daysToShift);

        const newDueDate = new Date(currentDueDate);
        newDueDate.setDate(currentDueDate.getDate() + daysToShift);

        updates.start_date = newStartDate.toISOString().split('T')[0];
        updates.due_date = newDueDate.toISOString().split('T')[0];
      } else if (dragMode === 'resize-left') {
        // Adjust start date only
        const currentStartDate = draggingTask.start_date ? new Date(draggingTask.start_date) : (draggingTask.created_at ? new Date(draggingTask.created_at) : new Date());
        const newStartDate = new Date(currentStartDate);
        newStartDate.setDate(currentStartDate.getDate() + daysToShift);

        // Get the due date for validation
        const dueDate = draggingTask.due_date ? new Date(draggingTask.due_date) : null;

        // Validate: new start date cannot be after due date
        if (dueDate && newStartDate > dueDate) {
          logger.warn('Cannot set start date after due date', { taskId: draggingTask.id, newStartDate, dueDate });
          setDraggingTask(null);
          setDragOffset(0);
          setDragMode(null);
          return;
        }

        updates.start_date = newStartDate.toISOString().split('T')[0];
      } else if (dragMode === 'resize-right') {
        // Adjust due date only
        const currentDueDate = draggingTask.due_date ? new Date(draggingTask.due_date) : new Date();
        const newDueDate = new Date(currentDueDate);
        newDueDate.setDate(currentDueDate.getDate() + daysToShift);

        // Get the start date for validation
        const startDate = draggingTask.start_date ? new Date(draggingTask.start_date) : null;

        // Validate: new due date cannot be before start date
        if (startDate && newDueDate < startDate) {
          logger.warn('Cannot set due date before start date', { taskId: draggingTask.id, newDueDate, startDate });
          setDraggingTask(null);
          setDragOffset(0);
          setDragMode(null);
          return;
        }

        updates.due_date = newDueDate.toISOString().split('T')[0];
      }

      // Optimistically update local state immediately
      setLocalTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === draggingTask.id ? { ...task, ...updates } : task
        )
      );

      // Reset drag state immediately for smooth UX
      setDraggingTask(null);
      setDragOffset(0);
      setDragMode(null);

      // Update backend in background
      logger.debug('Updating task in Gantt chart', { taskId: draggingTask.id, updates });
      onTaskUpdate({ taskId: draggingTask.id, updates })
        .then(() => {
          logger.debug('Task update successful', { taskId: draggingTask.id });
        })
        .catch(error => {
          logger.error('Error updating task date', {
            error,
            taskId: draggingTask.id,
            updates
          });
          // Revert on error by syncing with parent tasks
          setLocalTasks(tasks);
        });
    };

    if (draggingTask) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
      };
    }
  }, [draggingTask, dragOffset, dragStartX, columnWidth, zoomLevel, onTaskUpdate]);

  const timelineWidth = timeColumns.length * columnWidth;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 p-3 sm:p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center gap-2">
          <Calendar className="text-blue-600" size={18} />
          <h3 className="font-bold text-gray-900 text-sm sm:text-base">Gantt Chart</h3>
          <span className="text-xs text-gray-500">({localTasks.length} tasks)</span>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <div className="flex items-center gap-0.5 sm:gap-1 bg-white rounded-lg shadow-sm border border-gray-200 p-0.5 sm:p-1">
            <button
              onClick={handleZoomOut}
              className="p-1 sm:p-1.5 hover:bg-gray-100 rounded transition"
              disabled={zoomLevel === 'quarter'}
            >
              <ZoomOut size={16} className={zoomLevel === 'quarter' ? 'text-gray-300' : 'text-gray-600'} />
            </button>
            <span className="text-xs font-medium text-gray-600 px-1 sm:px-2 min-w-[50px] sm:min-w-[60px] text-center">
              {zoomLevel.charAt(0).toUpperCase() + zoomLevel.slice(1)}
            </span>
            <button
              onClick={handleZoomIn}
              className="p-1 sm:p-1.5 hover:bg-gray-100 rounded transition"
              disabled={zoomLevel === 'day'}
            >
              <ZoomIn size={16} className={zoomLevel === 'day' ? 'text-gray-300' : 'text-gray-600'} />
            </button>
          </div>

          <button
            onClick={handleFitToScreen}
            className="p-1.5 sm:p-2 bg-white hover:bg-gray-100 rounded-lg transition shadow-sm border border-gray-200"
          >
            <Maximize2 size={16} className="text-gray-600" />
          </button>

          <button
            onClick={handleJumpToToday}
            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition shadow-sm"
          >
            Today
          </button>

          <button
            onClick={handleExport}
            className="p-1.5 sm:p-2 bg-white hover:bg-gray-100 rounded-lg transition shadow-sm border border-gray-200 hidden sm:block"
          >
            <Download size={16} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="flex flex-col">
        {/* Headers - Fixed */}
        <div className="flex">
          {/* Task names header */}
          <div className="flex-shrink-0 bg-gray-800 border-r border-gray-600" style={{ width: TASK_NAME_WIDTH }}>
            <div className="h-8 flex items-center px-2 sm:px-4 border-b border-gray-600">
              <span className="text-white font-bold text-xs">Year</span>
            </div>
            <div className="h-12 flex items-center px-2 sm:px-4">
              <span className="text-white font-bold text-xs sm:text-sm">Task Name</span>
            </div>
          </div>

          {/* Timeline header */}
          <div ref={headerRef} className="flex-1 overflow-x-hidden bg-gray-100">
            <div style={{ width: timelineWidth, minWidth: '100%' }}>
              {/* Year row */}
              <div className="h-8 border-b border-gray-300 flex bg-gray-700">
                {yearHeaders.map((yearHeader, idx) => (
                  <div
                    key={idx}
                    className="border-r border-gray-500 flex items-center justify-center bg-gray-700"
                    style={{ width: yearHeader.width, minWidth: yearHeader.width }}
                  >
                    <span className="text-sm font-bold text-white">
                      {yearHeader.year}
                    </span>
                  </div>
                ))}
              </div>
              {/* Date/Month row */}
              <div className="h-12 border-b border-gray-300 flex">
                {timeColumns.map((col, idx) => (
                  <div
                    key={idx}
                    className={`border-r border-gray-200 flex items-center justify-center ${
                      col.isToday ? 'bg-yellow-100' : col.isWeekend ? 'bg-gray-50' : 'bg-white'
                    }`}
                    style={{ width: columnWidth, minWidth: columnWidth }}
                  >
                    <span className={`text-xs font-medium ${col.isToday ? 'text-yellow-900 font-bold' : 'text-gray-600'}`}>
                      {col.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area - Task names frozen, timeline scrolls */}
        <div className="flex" style={{ maxHeight: window.innerWidth < 640 ? '400px' : '600px' }}>
          {/* Task names column - Fixed/Frozen */}
          <div
            ref={taskNamesRef}
            className="flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto overflow-x-hidden"
            style={{ width: TASK_NAME_WIDTH, maxHeight: window.innerWidth < 640 ? '400px' : '600px' }}
            onScroll={(e) => {
              // Sync vertical scroll with timeline
              if (chartRef.current) {
                chartRef.current.scrollTop = e.currentTarget.scrollTop;
              }
            }}
          >
            {visibleTasks.map((item, idx) => (
              <div
                key={item.isGroup ? `group-${item.name}` : `task-${item.id}`}
                className={`border-b border-gray-200 ${item.isGroup ? 'bg-gray-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                style={{ height: ROW_HEIGHT }}
              >
                {item.isGroup ? (
                  <button
                    onClick={() => toggleGroup(item.name)}
                    className="w-full h-full px-4 flex items-center gap-2 hover:bg-gray-100 transition text-left"
                  >
                    {collapsedGroups[item.name] ? (
                      <ChevronRight size={16} className="text-gray-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-600 flex-shrink-0" />
                    )}
                    <span className="font-bold text-sm text-gray-900 truncate">
                      {item.name} ({item.count})
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => onTaskClick && onTaskClick(item)}
                    className="h-full px-4 flex items-center w-full text-left hover:bg-blue-50 transition cursor-pointer"
                  >
                    <span className="text-sm text-gray-700 leading-tight" title={item.title}>
                      {item.title || 'Untitled Task'}
                    </span>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Timeline - Scrollable horizontally and vertically */}
          <div
            ref={chartRef}
            className="flex-1 overflow-auto"
            style={{ maxHeight: window.innerWidth < 640 ? '400px' : '600px' }}
            onScroll={(e) => {
              // Sync horizontal scroll with header
              if (headerRef.current) {
                headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
              // Sync vertical scroll with task names
              if (taskNamesRef.current) {
                taskNamesRef.current.scrollTop = e.currentTarget.scrollTop;
              }
            }}
          >
            <div style={{ width: timelineWidth, minWidth: '100%' }}>
              {/* Task bars */}
            <div className="relative">
              {/* Background columns */}
              <div className="absolute inset-0 flex">
                {timeColumns.map((col, idx) => (
                  <div
                    key={idx}
                    className={`border-r border-gray-200 ${
                      col.isWeekend ? 'bg-gray-50' : 'bg-white'
                    }`}
                    style={{ width: columnWidth, minWidth: columnWidth }}
                  />
                ))}
              </div>

              {/* Today line */}
              {timeColumns.map((col, idx) => {
                if (col.isToday) {
                  return (
                    <div
                      key={`today-${idx}`}
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                      style={{ left: idx * columnWidth + columnWidth / 2 }}
                    />
                  );
                }
                return null;
              })}

              {/* Task bars */}
              {visibleTasks.map((item, idx) => {
                if (item.isGroup) {
                  return (
                    <div
                      key={`group-row-${item.name}`}
                      className="border-b border-gray-200 bg-gray-50"
                      style={{ height: ROW_HEIGHT }}
                    />
                  );
                }

                const { x, width } = getTaskBarDimensions(item);
                const progress = getProgress(item);
                const statusColor = getStatusColor(item.status);
                const priorityColor = getPriorityColor(item.priority);

                // Calculate position with drag offset if this task is being dragged
                const isDragging = draggingTask && draggingTask.id === item.id;
                let finalX = x;
                let finalWidth = width;

                if (isDragging) {
                  if (dragMode === 'resize-left') {
                    finalX = x + dragOffset;
                    finalWidth = width - dragOffset;
                  } else if (dragMode === 'resize-right') {
                    finalWidth = width + dragOffset;
                  } else {
                    // move mode
                    finalX = x + dragOffset;
                  }
                }

                return (
                  <div
                    key={`bar-${item.id}`}
                    className="relative border-b border-gray-200"
                    style={{ height: ROW_HEIGHT }}
                  >
                    <div
                      className={`absolute top-2 h-8 rounded border-2 ${statusColor} cursor-move hover:shadow-lg transition-shadow ${isDragging ? 'opacity-70 shadow-2xl z-50' : ''}`}
                      style={{
                        left: finalX,
                        width: Math.max(finalWidth, 30),
                      }}
                      onMouseDown={(e) => handleTaskBarMouseDown(e, item, 'move')}
                      onClick={(e) => {
                        if (!isDragging && dragOffset === 0) {
                          onTaskClick && onTaskClick(item);
                        }
                      }}
                    >
                      {/* Left resize handle */}
                      <div
                        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-blue-500 hover:opacity-50 z-10"
                        onMouseDown={(e) => handleTaskBarMouseDown(e, item, 'resize-left')}
                        onClick={(e) => e.stopPropagation()}
                      />

                      {/* Priority bar */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${priorityColor} rounded-l`} />

                      {/* Progress fill */}
                      {progress > 0 && (
                        <div
                          className="absolute top-0 left-0 bottom-0 bg-blue-500 opacity-30 rounded"
                          style={{ width: `${progress}%` }}
                        />
                      )}

                      {/* Progress text */}
                      {width > 60 && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-semibold text-gray-700">{progress}%</span>
                        </div>
                      )}

                      {/* Right resize handle */}
                      <div
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-blue-500 hover:opacity-50 z-10"
                        onMouseDown={(e) => handleTaskBarMouseDown(e, item, 'resize-right')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
